import React from 'react';
import block from 'bem-cn-lite';
import {useFieldArray} from 'react-final-form-arrays';

import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {ComputeConfig, ComputeDisk} from 'components/calculator/models';
import AddButton from 'components/Buttons/AddButton/AddButton';

import ComputeFormDiskFields from 'components/calculator/ComputeFormFields/ComputeFormDiskFields';
import CoreFractionField from 'components/calculator/ComputeFormFields/fields/CoreFractionField/CoreFractionField';
import CPUCoresField from 'components/calculator/ComputeFormFields/fields/CPUCoresField/CPUCoresField';
import GPUCoresField from 'components/calculator/ComputeFormFields/fields/GPUCoresField/GPUCoresField';
import MemoryField from 'components/calculator/ComputeFormFields/fields/MemoryField/MemoryField';
import NetworkField from 'components/calculator/ComputeFormFields/fields/NetworkField/NetworkField';
import OSProductField from 'components/calculator/ComputeFormFields/fields/OSProductField/OSProductField';
import PlatformField from 'components/calculator/ComputeFormFields/fields/PlatformField/PlatformField';
import PreemptibleField from 'components/calculator/ComputeFormFields/fields/PreemptibleField/PreemptibleField';
import TrafficField from 'components/calculator/Traffic/TrafficField/TrafficField';

import {getInitialDiskValues} from 'components/calculator/ComputeFormFields/utils';

import './ComputeFormFields.scss';

const b = block('ComputeFormFields');

export interface ComputeFormFieldsValues {
    osProduct: string;
    platform: string;
    coreFraction: number;
    cores: number;
    gpuCores?: number;
    memory: number;
    preemptible?: boolean;
    network?: boolean;
    traffic: number;
    disks: ComputeDisk[];
}

interface ComputeFormFieldsDisableProps {
    disableRepeatableDisks?: boolean;
    disableOperationSystem?: boolean;
    disableTraffic?: boolean;
}

interface ComputeFormFieldsOwnProps {
    fieldName?: string;
    customOsProductId?: string;
    values: ComputeFormFieldsValues;
}

export type ComputeFormFieldsProps = ComputeFormFieldsOwnProps &
    ComputeConfig &
    ComputeFormFieldsDisableProps &
    WithTranslationProps;

const ComputeFormFields: React.FC<ComputeFormFieldsProps> = (props) => {
    const {
        fieldName,
        customOsProductId,
        disableOperationSystem,
        disableRepeatableDisks,
        disableTraffic,
        i18nK,
        platforms,
        osProducts,
        values,
    } = props;

    const name = fieldName ? `${fieldName}.` : '';

    const gpuPlatforms = React.useMemo(
        () =>
            platforms
                .filter(
                    ({allowedGpuConfigurations}) =>
                        allowedGpuConfigurations && allowedGpuConfigurations.length > 0,
                )
                .map(({id}) => id),
        [platforms],
    );

    const {fields} = useFieldArray<ComputeDisk>(`${name}disks`);

    return (
        <React.Fragment>
            <CalculatorFormLayout.Section>
                {!customOsProductId && !disableOperationSystem && (
                    <OSProductField name={name} osProducts={osProducts} />
                )}
                <PlatformField name={name} platforms={platforms} gpuPlatforms={gpuPlatforms} />
                {gpuPlatforms.includes(values.platform) ? (
                    <div className={b('cpu-settings')}>
                        <GPUCoresField values={values} name={name} platforms={platforms} />
                        <CPUCoresField
                            disabled
                            values={values}
                            name={name}
                            platforms={platforms}
                            osProducts={osProducts}
                        />
                        <MemoryField
                            disabled
                            values={values}
                            name={name}
                            platforms={platforms}
                            osProducts={osProducts}
                        />
                    </div>
                ) : (
                    <div className={b('cpu-settings')}>
                        <CoreFractionField values={values} name={name} platforms={platforms} />
                        <CPUCoresField
                            values={values}
                            name={name}
                            platforms={platforms}
                            osProducts={osProducts}
                        />
                        <MemoryField
                            values={values}
                            name={name}
                            platforms={platforms}
                            osProducts={osProducts}
                        />
                    </div>
                )}
                <PreemptibleField name={name} />
                <NetworkField name={name} />
                {!disableTraffic && <TrafficField />}
            </CalculatorFormLayout.Section>
            {fields.map((disk, index) => (
                <ComputeFormDiskFields
                    key={index}
                    name={disk}
                    index={index}
                    onClose={() => fields.remove(index)}
                    osProducts={osProducts}
                    disableRepeatableDisks={disableRepeatableDisks}
                />
            ))}
            {!disableRepeatableDisks && (
                <CalculatorFormLayout.Section>
                    <AddButton onClick={() => fields.push(getInitialDiskValues())}>
                        {i18nK('button_add-disk')}
                    </AddButton>
                </CalculatorFormLayout.Section>
            )}
        </React.Fragment>
    );
};

export default withTranslation('calculator')(ComputeFormFields);
